import {useCallback, useEffect, useRef, useState} from "react";
import type {INumericFieldSettingsDefinition} from "../../../../data/models/field/datatypes/Numeric";
import {useAppStore} from "../../../../StateManager";
import {Functions} from "../../../../utils/function/Functions";
import type {TransformObj} from "../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import {SVGIcon} from "../../button/SVGIcon";
import {DomPortal} from "../../../modules/abstract/portal/DomPortal";
import {InfoBubble} from "../../../modules/abstract/common/infobutton/InfoBubble";
import {ReactUtils} from "../../../utils/ReactUtils";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {MathUtils} from "../../../../utils/math/MathUtils";
import {ObjectUtils} from "../../../../utils/data/ObjectUtils";

interface INumberInputProps {
	readonly min?: number;
	readonly max?: number;
	readonly value?: number;
	readonly onInput?: (value: number | string) => void;
	readonly onChange?: (value: number) => void;
	readonly onBlur?: () => void;
	readonly decimals?: number;
	readonly disabled?: boolean;
	readonly className?: string;
	readonly autoFocus?: boolean;
	readonly dataTypeSettings?: INumericFieldSettingsDefinition;
	readonly noButtons?: boolean;
	readonly caretPosition?: number;
	readonly onlyNaturalNumbers?: boolean;
}

const getInitialEditingValue = (value: number, dataTypeSettings: INumericFieldSettingsDefinition, decimals: number): string => {
	// value is undefined or null or "", but not 0
	if (value !== 0 && !value) {
		return "";
	}

	if (dataTypeSettings?.formatting === "percentage") {
		return (Number(value) * 100).toFixed(decimals);
	}

	return String(value);
};

const getEditingValueAsNumber = (value: string) => {
	if (!value) {
		return 0;
	}
	return parseFloat(value);
};

const getLastValidValue = (value: string, dataTypeSettings: INumericFieldSettingsDefinition) => {
	if (!value) {
		return null; // no record -> we send null to BE
	}

	const inputValue = parseFloat(value);
	const divident = dataTypeSettings?.formatting === "percentage" ? 100 : 1;
	const finalValue = inputValue === null ? null : inputValue / divident; // if a user wants to delete the value and leave it empty, we need to send null (value / divident) becomes 0 instead of null

	return isNaN(finalValue) ? 0 : finalValue;
};

export const NumberInput = (props: INumberInputProps) => {
	const {
		min,
		max,
		value = null,
		onInput = Functions.emptyFunction,
		onChange = Functions.emptyFunction,
		onBlur = Functions.emptyFunction,
		decimals = 2,
		disabled,
		className,
		autoFocus = true,
		dataTypeSettings,
		noButtons,
		caretPosition = 0,
		onlyNaturalNumbers = false,
	} = props;
	const _ref = useRef<HTMLInputElement>(null);
	const _floating = useRef<HTMLDivElement>(null);
	const _toolTip = useRef<HTMLDivElement>(null);
	const _requestAnimationFrameId = useRef<number>(-1);
	const _lastValidValue = useRef<number>(getLastValidValue(getInitialEditingValue(value, dataTypeSettings, decimals), dataTypeSettings));

	const appState = useAppStore((state) => state.appState);
	const [editingValue, setEditingValue] = useState<string>(getInitialEditingValue(value, dataTypeSettings, decimals));
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [tooltipTransform, setTooltipTransform] = useState<TransformObj>(null);

	const onInputString = (inputString: string) => {
		const errorMessage = appState.actions.getNumberInputErrorMessage(inputString, dataTypeSettings, onlyNaturalNumbers);
		const inputValue = getAPIUpdateNumber(inputString);

		setErrorMessage(errorMessage);
		setEditingValue(inputString);

		if (!errorMessage) {
			const divident = dataTypeSettings?.formatting === "percentage" ? 100 : 1;
			const finalValue = inputValue === null ? null : inputValue / divident; // if a user wants to delete the value and leave it empty, we need to send null (value / divident) becomes 0 instead of null

			_lastValidValue.current = finalValue;
		}
	};

	const onInputEvent = (event: React.InputEvent<HTMLInputElement>) => {
		onInputString(event.currentTarget.value);
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		let direction = 0;

		switch (event.key) {
			case KeyboardListener.KEY_DOWN:
				direction = -1;
				break;
			case KeyboardListener.KEY_UP:
				direction = 1;
				break;
			case KeyboardListener.KEY_ENTER:
				onBlurInput();
				break;
		}

		if (direction !== 0) {
			event.preventDefault();
			const stepSize = decimals >= 1 ? 0.1 : 1;
			const stepValue = direction * stepSize;
			const newValue = getEditingValueAsNumber(editingValue) + stepValue;

			onInputString(newValue.toFixed(decimals));
		}
	};

	const triggerChange = useCallback(() => {
		if (value !== _lastValidValue.current) {
			onInput(_lastValidValue.current);
			onChange(_lastValidValue.current);
		}
	}, [value, onInput, onChange]);

	const onBlurInput = () => {
		triggerChange();
		onBlur?.();
	};

	const getAPIUpdateNumber = (valueString: string) => {
		if (StringUtils.trim(valueString) === "") {
			// Note: very important to return null instead of undefined.
			// undefined doesn't work because in XHRLoader JSON.stringify converts {f35: undefined} -> {}
			// causing a bug that when a numberinput is cleared it's not saved.
			return null;
		}

		let valueAsFloat = parseFloat(valueString);

		valueAsFloat = MathUtils.clamp(valueAsFloat, min, max);

		return valueAsFloat;
	};

	const updateTooltipPosition = useCallback(() => {
		const input = _ref.current;
		const infoBubble = _floating.current;

		if (errorMessage && input && infoBubble) {
			const newTooltipTransform = DomUtils.getFixedFloatingElementPosition(input, infoBubble, VerticalAlignment.bottom, HorizontalAlignment.right);

			if (!ObjectUtils.compare(tooltipTransform, newTooltipTransform)) {
				setTooltipTransform(newTooltipTransform);
			}
		}
	}, [errorMessage, tooltipTransform]);

	const getTranslateY = (toolTipTransform: string) => {
		if (!toolTipTransform) {
			return null;
		}

		const translateStartIndex = toolTipTransform.indexOf("translate(");
		const commaIndex = toolTipTransform.indexOf(",", translateStartIndex + 10);
		const closingParenthesisIndex = toolTipTransform.indexOf(")", commaIndex + 1);

		if (translateStartIndex < 0 || commaIndex <= translateStartIndex + 10 || closingParenthesisIndex <= commaIndex + 1) {
			return null;
		}

		return parseFloat(toolTipTransform.slice(commaIndex + 1, closingParenthesisIndex));
	};

	useEffect(() => {
		const input = _ref.current;

		if (autoFocus) {
			input.focus();

			if (caretPosition !== undefined && input.setSelectionRange) {
				input.setSelectionRange(caretPosition, caretPosition);
			}
		}
	}, [autoFocus, caretPosition]);

	useEffect(() => {
		return () => {
			triggerChange();
		};
	}, [triggerChange]);

	useEffect(() => {
		if (autoFocus) {
			_ref.current.focus();
		}
	}, [autoFocus]);

	useEffect(() => {
		if (!errorMessage) {
			return;
		}

		const tick = () => {
			updateTooltipPosition();
			_requestAnimationFrameId.current = requestAnimationFrame(tick);
		};

		tick();

		return () => {
			if (_requestAnimationFrameId.current !== -1) {
				cancelAnimationFrame(_requestAnimationFrameId.current);
				_requestAnimationFrameId.current = -1;
			}
		};
	}, [errorMessage, updateTooltipPosition]);

	const inlineStyle: React.CSSProperties = {
		top: "-42px",
		left: "-180px",
		width: "15px",
		position: "absolute",
		zIndex: "9999",
		transform: tooltipTransform?.translate,
		visibility: getTranslateY(tooltipTransform?.translate) <= 231 ? "hidden" : "visible",
	};

	return (
		<>
			<input
				ref={_ref}
				className={className}
				value={editingValue}
				disabled={disabled}
				onBlur={onBlurInput}
				onInput={onInputEvent}
				onKeyDown={onKeyDown}
			/>
			{errorMessage && (
				<div
					ref={_floating}
					className={ReactUtils.cls("infoIcon editing", {left: noButtons})}
				>
					<div ref={_toolTip}>
						<SVGIcon icon="info" />
					</div>
					<DomPortal destination={appState.app.modalContainer}>
						<InfoBubble
							content={errorMessage}
							isErrorMessage={true}
							className={className}
							divRef={_toolTip}
							style={inlineStyle}
						/>
					</DomPortal>
				</div>
			)}
		</>
	);
};
